import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../context/SeoContext";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocalizedUrl,
  getLocaleFromPathname,
  getPathSegments,
  stripLocaleFromPathname,
} from "../utils/localeRouting";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://www.mafirol.com").replace(
  /\/+$/,
  ""
);
const SITE_NAME = "Mafirol";
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

const OG_LOCALE_MAP = {
  pt: "pt_PT",
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
};

const PAGE_COPY = {
  pt: {
    defaultTitle: "Mafirol",
    defaultDescription:
      "Grupo MAFIROL - Equipamentos hoteleiros e soluções de refrigeração comercial.",
    home: {
      title: "Mafirol | Equipamentos Hoteleiros e Refrigeração Comercial",
      description:
        "Conheça a Mafirol e descubra soluções para refrigeração comercial, mobiliário inox e equipamentos para hotelaria.",
    },
    products: {
      title: "Produtos | Mafirol",
      description:
        "Explore a gama de produtos Mafirol para retalho alimentar, hotelaria e refrigeração comercial.",
    },
    aboutus: {
      title: "Quem Somos | Mafirol",
      description:
        "Saiba mais sobre a história, os valores e a capacidade industrial do Grupo Mafirol.",
    },
    downloadarea: {
      title: "Área de Download | Mafirol",
      description:
        "Aceda a catálogos, fichas técnicas e documentos de apoio dos produtos Mafirol.",
    },
    recruitment: {
      title: "Recrutamento | Mafirol",
      description:
        "Consulte oportunidades de carreira e candidate-se para integrar a equipa Mafirol.",
    },
    contacts: {
      title: "Contactos | Mafirol",
      description:
        "Fale com a Mafirol, encontre unidades comerciais e solicite apoio técnico ou comercial.",
    },
    complaint: {
      title: "Canal de Denúncias | Mafirol",
      description:
        "Canal de denúncias da Mafirol para comunicação segura e confidencial de situações irregulares.",
    },
    account: {
      title: "Conta | Mafirol",
      description: "Área reservada para clientes e utilizadores registados.",
    },
  },
  en: {
    defaultTitle: "Mafirol",
    defaultDescription:
      "MAFIROL Group - Hospitality equipment and commercial refrigeration solutions.",
    home: {
      title: "Mafirol | Hospitality and Commercial Refrigeration Equipment",
      description:
        "Discover Mafirol solutions for commercial refrigeration, stainless steel furniture and hospitality equipment.",
    },
    products: {
      title: "Products | Mafirol",
      description:
        "Explore Mafirol products for food retail, hospitality and commercial refrigeration.",
    },
    aboutus: {
      title: "About Us | Mafirol",
      description:
        "Learn more about Mafirol Group history, values and industrial capability.",
    },
    downloadarea: {
      title: "Download Area | Mafirol",
      description:
        "Access catalogs, technical sheets and support documents for Mafirol products.",
    },
    recruitment: {
      title: "Recruitment | Mafirol",
      description: "Check career opportunities and apply to join the Mafirol team.",
    },
    contacts: {
      title: "Contacts | Mafirol",
      description:
        "Contact Mafirol, find commercial units and request technical or sales support.",
    },
    complaint: {
      title: "Whistleblowing Channel | Mafirol",
      description:
        "Mafirol whistleblowing channel for secure and confidential reporting.",
    },
    account: {
      title: "Account | Mafirol",
      description: "Private area for registered customers and users.",
    },
  },
  es: {
    defaultTitle: "Mafirol",
    defaultDescription:
      "Grupo MAFIROL - Equipamiento hostelero y soluciones de refrigeración comercial.",
    home: {
      title: "Mafirol | Equipamiento Hostelero y Refrigeración Comercial",
      description:
        "Descubra soluciones Mafirol para refrigeración comercial, mobiliario inox y hostelería.",
    },
    products: {
      title: "Productos | Mafirol",
      description:
        "Explore la gama de productos Mafirol para retail alimentario y hostelería.",
    },
    aboutus: {
      title: "Quiénes Somos | Mafirol",
      description:
        "Conozca la historia, los valores y la capacidad industrial del Grupo Mafirol.",
    },
    downloadarea: {
      title: "Área de Descargas | Mafirol",
      description:
        "Acceda a catálogos, fichas técnicas y documentos de apoyo de Mafirol.",
    },
    recruitment: {
      title: "Empleo | Mafirol",
      description:
        "Consulte oportunidades profesionales y envíe su candidatura a Mafirol.",
    },
    contacts: {
      title: "Contactos | Mafirol",
      description: "Contacte con Mafirol y encuentre soporte técnico y comercial.",
    },
    complaint: {
      title: "Canal de Denuncias | Mafirol",
      description:
        "Canal de denuncias de Mafirol para comunicaciones seguras y confidenciales.",
    },
    account: {
      title: "Cuenta | Mafirol",
      description: "Área privada para clientes y usuarios registrados.",
    },
  },
  fr: {
    defaultTitle: "Mafirol",
    defaultDescription:
      "Groupe MAFIROL - Équipements hôteliers et solutions de réfrigération commerciale.",
    home: {
      title: "Mafirol | Équipements Hôteliers et Réfrigération Commerciale",
      description:
        "Découvrez les solutions Mafirol pour la réfrigération commerciale et l'hôtellerie.",
    },
    products: {
      title: "Produits | Mafirol",
      description:
        "Explorez les produits Mafirol pour le commerce alimentaire et l'hôtellerie.",
    },
    aboutus: {
      title: "Qui Sommes-Nous | Mafirol",
      description:
        "Découvrez l'histoire, les valeurs et la capacité industrielle du Groupe Mafirol.",
    },
    downloadarea: {
      title: "Zone de Téléchargement | Mafirol",
      description:
        "Accédez aux catalogues, fiches techniques et documents des produits Mafirol.",
    },
    recruitment: {
      title: "Recrutement | Mafirol",
      description: "Consultez les opportunités et candidatez pour rejoindre Mafirol.",
    },
    contacts: {
      title: "Contacts | Mafirol",
      description: "Contactez Mafirol pour un support technique et commercial.",
    },
    complaint: {
      title: "Canal d'Alerte | Mafirol",
      description:
        "Canal d'alerte Mafirol pour signalements sécurisés et confidentiels.",
    },
    account: {
      title: "Compte | Mafirol",
      description: "Espace privé pour clients et utilisateurs enregistrés.",
    },
  },
};

const setMetaTag = (name, content, property = false) => {
  if (!content) return;

  const selector = property
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(property ? "property" : "name", name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setLinkTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("link");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const getRouteKey = (pathname) => {
  const parts = getPathSegments(pathname);
  const firstSegment = parts[0];
  return firstSegment || "home";
};

const getBreadcrumbs = (pathname, locale, pageTitle) => {
  const segments = getPathSegments(pathname);
  const breadcrumbs = [
    {
      name: SITE_NAME,
      url: getLocalizedUrl(SITE_URL, "/", locale),
    },
  ];

  segments.forEach((segment, index) => {
    breadcrumbs.push({
      name: index === segments.length - 1 && pageTitle ? pageTitle : segment,
      url: getLocalizedUrl(
        SITE_URL,
        `/${segments.slice(0, index + 1).join("/")}`,
        locale
      ),
    });
  });

  return breadcrumbs;
};

export const SeoManager = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const { pageSeo } = useSeo();

  const locale = useMemo(() => {
    const routeLocale = getLocaleFromPathname(pathname);
    const current = (routeLocale || i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE).slice(
      0,
      2
    );
    return PAGE_COPY[current] ? current : DEFAULT_LOCALE;
  }, [i18n.language, i18n.resolvedLanguage, pathname]);

  useEffect(() => {
    const routeKey = getRouteKey(pathname);
    const copy = PAGE_COPY[locale];
    const routeCopy = copy[routeKey] || {};
    const isPrivatePage = stripLocaleFromPathname(pathname).startsWith("/account");
    const pageTitle = pageSeo?.title || routeCopy.title || copy.defaultTitle;
    const description =
      pageSeo?.description || routeCopy.description || copy.defaultDescription;
    const image = pageSeo?.image || DEFAULT_IMAGE;
    const canonicalPath = pageSeo?.canonicalPath || pathname;
    const canonicalUrl = getLocalizedUrl(SITE_URL, canonicalPath, locale);
    const robots = pageSeo?.robots || (isPrivatePage || pageSeo?.noindex ? "noindex,nofollow" : "index,follow");
    const breadcrumbs = getBreadcrumbs(canonicalPath, locale, pageTitle);

    document.title = pageTitle;
    document.documentElement.setAttribute("lang", locale);

    setMetaTag("description", description);
    setMetaTag("robots", robots);
    setMetaTag("og:title", pageTitle, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:type", pageSeo?.type || "website", true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:site_name", SITE_NAME, true);
    setMetaTag("og:locale", OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.pt, true);
    setMetaTag("og:image", image, true);
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", image);

    setLinkTag('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });

    SUPPORTED_LOCALES.forEach((language) => {
      setLinkTag(`link[rel="alternate"][hreflang="${language}"]`, {
        rel: "alternate",
        hreflang: language,
        href: getLocalizedUrl(SITE_URL, canonicalPath, language),
      });
    });

    setLinkTag('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: getLocalizedUrl(SITE_URL, canonicalPath, DEFAULT_LOCALE),
    });

    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${SITE_URL}#organization`,
          name: "Grupo MAFIROL",
          url: SITE_URL,
          logo: `${SITE_URL}/logo192.png`,
          email: "inform@mafirol.pt",
          telephone: "+351234610480",
          sameAs: [
            "https://www.facebook.com/mafirol/",
            "https://www.linkedin.com/company/grupo-mafirol/",
            "https://www.youtube.com/@GrupoMAFIROL",
            "https://www.instagram.com/grupomafirol/",
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Rua Alto do Vale do Grou, n. 976, Borralha",
            postalCode: "3750-872",
            addressLocality: "Agueda",
            addressCountry: "PT",
          },
        },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}#website`,
          url: SITE_URL,
          name: SITE_NAME,
          inLanguage: locale,
          publisher: {
            "@id": `${SITE_URL}#organization`,
          },
        },
        {
          "@type": "WebPage",
          "@id": `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: pageTitle,
          description,
          isPartOf: {
            "@id": `${SITE_URL}#website`,
          },
          inLanguage: locale,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        },
        ...((pageSeo?.jsonLd || []).filter(Boolean)),
      ],
    };

    let scriptTag = document.head.querySelector('script[id="seo-structured-data"]');
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.setAttribute("id", "seo-structured-data");
      scriptTag.setAttribute("type", "application/ld+json");
      document.head.appendChild(scriptTag);
    }

    scriptTag.textContent = JSON.stringify(jsonLd);
  }, [i18n.language, locale, pageSeo, pathname]);

  return null;
};
